import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { loadMermaid } from "@/lib/viz/cdnLoader";
import type { VizRenderPayload } from "@/lib/viz/types";

interface Props {
  payload: VizRenderPayload;
}

/**
 * Renders a Mermaid spec (currently used for ERDs emitted by
 * renderDataModuleSchema, but Mermaid handles flowchart / sequence /
 * gantt etc. with the same surface). Loads mermaid on demand via the
 * shared CDN loader; failures surface inline instead of crashing the
 * chat thread.
 *
 * <p>Mermaid uses a static id-namespaced approach: each diagram needs a
 * unique container id so concurrent renders don't collide. We derive
 * one from a monotonically-increasing counter inside this module — no
 * dependency on the spec text (two charts with identical specs need
 * distinct ids, otherwise mermaid silently reuses the first one's
 * cached output).
 */
let mermaidIdCounter = 0;

export const MermaidRenderer: React.FC<Props> = ({ payload }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [svg, setSvg]     = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const renderId = `mermaid-erd-${++mermaidIdCounter}`;

    (async () => {
      try {
        const mermaid = await loadMermaid();
        // initialize() is idempotent — mermaid no-ops on repeat calls
        // with the same config. Safe to invoke per-render.
        //
        // useMaxWidth=false on every diagram family: the default
        // `useMaxWidth: true` makes Mermaid emit an SVG with
        // `style="max-width: <naturalPx>"`, which CAPS the rendered
        // size at the layout-engine's natural width — typically much
        // smaller than the chat-bubble / fullscreen viewport. The
        // diagram renders tiny in the corner and the VizChrome zoom
        // can't grow it because CSS transforms scale visually but
        // don't override the max-width style. With useMaxWidth=false,
        // Mermaid emits a plain width/height pair we then rewrite
        // (post-render below) to fill the container.
        mermaid.initialize({
          startOnLoad: false,
          theme: "neutral",
          securityLevel: "loose",
          er:        { useMaxWidth: false },
          flowchart: { useMaxWidth: false },
          sequence:  { useMaxWidth: false },
          gantt:     { useMaxWidth: false },
        });
        let result = await mermaid.render(renderId, payload.specJson);
        if (!cancelled) {
          // Strip the max-width style + force responsive sizing so the
          // SVG fills the chrome's content area (and scales with the
          // outer transform when the user zooms).
          const fluidSvg = makeSvgResponsive(result.svg);
          setSvg(fluidSvg);
          if (result.bindFunctions && containerRef.current) {
            requestAnimationFrame(() => {
              if (cancelled || !containerRef.current) return;
              try {
                result.bindFunctions?.(containerRef.current);
              } catch {
                /* bindFunctions is non-essential — log silently */
              }
            });
          }
        }
      } catch (e) {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : "unknown error";
          setError(msg);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [payload]);

  if (error) {
    return (
      <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
        <div className="font-semibold">Mermaid diagram could not render</div>
        <div className="mt-1 text-xs text-rose-700">{error}</div>
        <details className="mt-2 text-xs text-rose-700">
          <summary className="cursor-pointer select-none">Show raw spec</summary>
          <pre className="mt-1 whitespace-pre-wrap break-all rounded bg-rose-100 p-2 font-mono text-[10.5px]">
            {payload.specJson}
          </pre>
        </details>
      </div>
    );
  }

  // After Mermaid mounts the SVG, apply per-table CSS classes derived
  // from the `%% dc-class:<entityId>=<tableTypeToken>` sidecar
  // comments in the spec, then inject a <style> block that gives
  // each table type its accent color. This is layered on top of the
  // brand-palette `themeVariables` block that the backend prepends
  // — base look is uniform DatumCraft navy/teal; per-table headers
  // pick up role-specific colors so fact tables read distinctly from
  // dimensions and utility tables at a glance.
  useEffect(() => {
    if (!containerRef.current || !svg) return;
    const container = containerRef.current;
    const svgEl = container.querySelector("svg");
    if (!svgEl) return;
    const typeByEntity = parseDcClassComments(payload.specJson);
    if (typeByEntity.size === 0) return;
    // Mermaid v10's erDiagram emits each entity as a <g> with class
    // "node" or "entityBox" containing both the header label and the
    // attribute rows. The element's id starts with "entity-" followed
    // by the sanitized entity name. We match by the trailing-name
    // portion and tag the whole entity group with a dc-* class.
    const entities = svgEl.querySelectorAll('g[id]');
    entities.forEach((el) => {
      const id = el.getAttribute('id') ?? '';
      // Mermaid id patterns vary across versions: "entity-Product",
      // "Product", "entity-Product-1", etc. We try multiple matches
      // against our type-name map, picking the longest match to avoid
      // a "Foo" map entry matching an "entity-FooBar" id.
      let bestMatch: string | null = null;
      for (const name of typeByEntity.keys()) {
        if (id === name || id.endsWith(`-${name}`) || id.includes(`-${name}-`)) {
          if (!bestMatch || name.length > bestMatch.length) bestMatch = name;
        }
      }
      if (bestMatch) {
        const klass = typeByEntity.get(bestMatch);
        if (klass) el.classList.add(`dc-${klass}`);
      }
    });
    // Inject a single <style> block with per-class overrides.
    //
    // Earlier versions filled `rect.er.entityBox` with a dark per-type
    // color, on the assumption it was just the title bar. It isn't —
    // Mermaid's entityBox spans the WHOLE table, so fact/dimension
    // tables were painted navy/steel top-to-bottom and their dark
    // attribute text rendered dark-on-dark (faded/invisible), while
    // untyped tables stayed light and readable. That's the
    // inconsistency operators reported.
    //
    // Fix: keep a uniform LIGHT body on every table (so a row can never
    // be dark-on-dark) and convey table type with a colored BORDER
    // accent instead of a dark fill. All text is forced dark navy, so
    // contrast is identical across fact / dimension / utility tables.
    if (svgEl.querySelector('style.dc-table-types')) return; // idempotent
    const styleEl = document.createElementNS('http://www.w3.org/2000/svg', 'style');
    styleEl.setAttribute('class', 'dc-table-types');
    styleEl.textContent = `
      /* Uniform light body; type shown via border color (navy / steel /
         teal). entityBox is the whole table box, so it MUST stay light. */
      .dc-factTable rect.er.entityBox      { fill: #f7f9fc !important; stroke: #0b2545 !important; stroke-width: 2.5px !important; }
      .dc-dimensionTable rect.er.entityBox { fill: #f7f9fc !important; stroke: #3b5d85 !important; stroke-width: 2.5px !important; }
      .dc-utilityTable rect.er.entityBox   { fill: #f7f9fc !important; stroke: #14b8a6 !important; stroke-width: 2.5px !important; }

      /* Attribute-row backgrounds (any non-entityBox rect under a typed
         table) forced light too — belt-and-suspenders against a Mermaid
         version that tints the row strips. */
      .dc-factTable rect:not(.entityBox),
      .dc-dimensionTable rect:not(.entityBox),
      .dc-utilityTable rect:not(.entityBox) { fill: #ffffff !important; }

      /* ALL text dark navy on the light body — readable everywhere,
         identical across table types. The header label is just bolder.
         Targeting every text node (not enumerating Mermaid's per-version
         attribute class names) keeps this robust across versions. */
      .dc-factTable text,
      .dc-dimensionTable text,
      .dc-utilityTable text { fill: #0b2545 !important; }
      .dc-factTable text.er.entityLabel,
      .dc-dimensionTable text.er.entityLabel,
      .dc-utilityTable text.er.entityLabel { font-weight: 700 !important; }
    `;
    svgEl.insertBefore(styleEl, svgEl.firstChild);
  }, [svg, payload.specJson]);

  // After Mermaid emits the SVG and React mounts it, pin the SVG's
  // intrinsic pixel width ONCE to the container's current width.
  //
  // Why we need this: VizChrome zooms by setting `transform: scale(N)`
  // on a wrapper div + `width: max-content` (when N > 1) for scrollbar
  // calculation. An SVG with `width="100%"` inside a `max-content`
  // parent collapses to its viewBox's natural pixel size, defeating
  // the transform.
  //
  // Why we pin ONCE (not via ResizeObserver): a previous version used
  // a ResizeObserver to keep the pinned width in sync with the
  // container. That created a feedback loop — VizChrome's transform
  // caused scrollbar appearance which shrank the container by ~15px,
  // which triggered the observer, which re-pinned the SVG, which
  // re-triggered the chrome's scrollbar reflow, etc. The diagram
  // grew uncontrollably with each zoom click. Pinning once at mount
  // is the stable choice: the SVG width is fixed for the lifetime of
  // this render, the chrome's transform scales it predictably, and
  // there's no observer loop to amplify spurious resize events.
  //
  // The cost: a window resize or fullscreen toggle WON'T re-fit the
  // SVG — the user would need to re-issue the chart. Worth it for
  // working zoom controls.
  useEffect(() => {
    if (!containerRef.current || !svg) return;
    const container = containerRef.current;
    const svgEl = container.querySelector("svg");
    if (!svgEl) return;
    const width = container.clientWidth;
    if (width < 1) return;
    svgEl.setAttribute("width", String(width));
    // Drop any inline height so the browser derives it from the
    // viewBox aspect ratio — keeps diagrams from squashing.
    svgEl.removeAttribute("height");
  }, [svg]);

  return (
    <div
      ref={containerRef}
      // No overflow / max-height here on purpose — VizChrome owns the
      // outer scroll container and the zoom transform. A nested
      // overflow:auto would steal wheel events from the chrome and
      // clip scaled content. Min-height keeps the bubble from
      // collapsing while mermaid is still loading.
      className="w-full min-h-[280px] bg-white p-3"
      // Mermaid emits standalone SVG; injecting via innerHTML is safe
      // because the spec is server-controlled (see initialize() comment
      // above). React's dangerouslySetInnerHTML is the standard way to
      // mount third-party-rendered SVG into a React tree.
      dangerouslySetInnerHTML={svg ? { __html: svg } : undefined}
    />
  );
};

/**
 * Strip Mermaid's natural-width cap and force the SVG to be responsive
 * so it fills the chrome content area (and scales cleanly under the
 * outer zoom transform).
 *
 * <p>Mermaid emits an SVG header that looks like:
 * <pre>
 *   &lt;svg ... width="800" height="500"
 *        viewBox="0 0 800 500"
 *        style="max-width: 800px; background-color: white;" ...&gt;
 * </pre>
 * The {@code max-width} style is the offender — it caps the rendered
 * size at the layout-engine's natural width regardless of the
 * container's actual width.
 *
 * <p>This helper deletes the {@code max-width} declaration and rewrites
 * the explicit {@code width="..."} attribute to {@code width="100%"}
 * while preserving the {@code viewBox} (so the SVG scales
 * proportionally without distorting). The {@code height} attribute is
 * dropped — browsers compute it from the viewBox aspect ratio under
 * {@code width: 100%}.
 *
 * <p>String-level surgery (rather than DOM-parse) keeps the helper
 * dependency-free and avoids round-tripping through an XML parser
 * that might reformat the SVG body in unexpected ways. The pattern
 * targets Mermaid's known header shape; any non-matching SVG passes
 * through untouched.
 */
/**
 * Parse the `%% dc-class:<entityId>=<tableTypeToken>` sidecar lines
 * the backend appends to every erDiagram spec. Mermaid silently
 * ignores any line starting with `%%` (those are mermaid comments),
 * so we use them as a side-channel for the per-entity classification
 * the rendered SVG can't carry on its own.
 *
 * <p>Returns an empty map when no lines match — older backend
 * versions (or any non-ERD diagram) won't emit them and the
 * frontend should degrade to the uniform brand palette without
 * error.
 */
function parseDcClassComments(spec: string): Map<string, string> {
  const out = new Map<string, string>();
  const lines = spec.split(/\r?\n/);
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed.startsWith("%% dc-class:")) continue;
    const body = trimmed.substring("%% dc-class:".length);
    const eq = body.indexOf("=");
    if (eq < 1) continue;
    const entity = body.substring(0, eq).trim();
    const klass  = body.substring(eq + 1).trim();
    if (entity && klass) out.set(entity, klass);
  }
  return out;
}

function makeSvgResponsive(rawSvg: string): string {
  if (!rawSvg.startsWith("<svg")) return rawSvg;
  const headerEnd = rawSvg.indexOf(">");
  if (headerEnd < 0) return rawSvg;
  let header = rawSvg.substring(0, headerEnd);
  const rest   = rawSvg.substring(headerEnd);
  // Remove the max-width: Npx; declaration from the style attribute.
  header = header.replace(/max-width:\s*\d+(?:\.\d+)?px\s*;?\s*/g, "");
  // If the style attribute became empty after stripping, drop it.
  header = header.replace(/style="\s*"/g, "");
  // Force width:100% and drop explicit height — preserves the viewBox
  // aspect ratio while letting the container drive layout.
  header = header.replace(/\swidth="[^"]*"/, ' width="100%"');
  header = header.replace(/\sheight="[^"]*"/, "");
  return header + rest;
}
